import React from 'react';
import {
  Container,
  Typography,
  Box,
  Divider,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import SEOHead from '../components/SEOHead';

function PoliticasPrivacidad() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <SEOHead
        title="Políticas de Privacidad - VoyBus"
        description="Políticas de privacidad de VoyBus. Conoce cómo protegemos y utilizamos tu información personal."
        keywords={['política de privacidad', 'privacidad', 'protección de datos', 'VoyBus']}
        url="/politicas-privacidad"
        type="website"
        noindex={true}
      />
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            .print-content, .print-content * {
              visibility: visible;
            }
            .print-content {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              background: white;
            }
            .no-print {
              display: none !important;
            }
          }
        `}
      </style>
      <Box sx={{ minHeight: '100vh', backgroundColor: '#fafafa', py: 4 }} className="print-content">
      <Container maxWidth="lg">
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1.4rem', sm: '1.75rem', md: '2.5rem' },
            mb: 2,
            color: 'text.primary',
          }}
        >
          🔒 Política de Privacidad de VOYBUS.COM
        </Typography>

        <Box sx={{ mb: 4 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Última actualización: {new Date().toLocaleDateString('es-UY')}
          </Typography>

          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
            En VOYBUS.COM valoramos tu confianza y queremos que te sientas seguro cuando navegás por nuestro sitio o nos compartís tus datos. Por eso, te contamos de forma clara y sencilla cómo usamos tu información y para qué.
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8, fontStyle: 'italic' }}>
            Al usar nuestro sitio o compartir tus datos con nosotros, estás aceptando esta Política de Privacidad.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}
          >
            👥 1. ¿Quiénes somos?
          </Typography>
          <Box sx={{ pl: { xs: 2, md: 3 }, mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              <strong>Nombre Fantasía:</strong> VOYBUS.COM
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              <strong>Razón Social:</strong> AMERICA EN BUS S.R.L.
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              <strong>Dirección:</strong> Rafael Pérez del Puerto 1037, Maldonado – Uruguay
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              <strong>Email:</strong> hola@voybus.com
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ mt: 2, mb: 2, lineHeight: 1.8 }}>
            Somos una empresa dedicada a la venta y comercialización de servicios turísticos.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}
          >
            📍 2. Qué datos podemos recopilar
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Podemos recopilar información personal cuando navegás por el sitio, nos contactás o hacés una reserva, por ejemplo:
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, fontWeight: 600 }}>
            Datos que podrías compartir con nosotros:
          </Typography>
          <Box sx={{ pl: { xs: 2, md: 3 }, mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              • Nombre y apellido
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              • Teléfono o WhatsApp
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              • Email
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
              • Información necesaria para una consulta o reserva
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, fontWeight: 600 }}>
            Datos que se recopilan automáticamente (de forma anónima o estadística):
          </Typography>
          <Box sx={{ pl: { xs: 2, md: 3 }, mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              • Páginas visitadas, tiempo de navegación, clicks
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              • Tipo de dispositivo, navegador y sistema operativo
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
              • Información técnica y de uso del sitio
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Esta info se usa para mejorar tu experiencia y entender qué contenidos son más útiles para nuestros usuarios.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}
          >
            🎯 3. Para qué usamos tus datos
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Usamos tu información para:
          </Typography>
          <Box sx={{ pl: { xs: 2, md: 3 }, mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              • Responder consultas y brindarte asistencia
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              • Gestionar reservas o compras que realices
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              • Enviarte información sobre tu viaje
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              • Mejorar nuestra web y servicios
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
              • Enviar comunicaciones comerciales solo si nos autorizás
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, fontStyle: 'italic' }}>
            Nunca vendemos tu información personal.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}
          >
            🤝 4. Con quién podemos compartir tu información
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Solo compartimos tu información cuando es necesario para brindarte el servicio, por ejemplo:
          </Typography>
          <Box sx={{ pl: { xs: 2, md: 3 }, mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              • Operadores turísticos
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              • Hoteles o empresas de transporte
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
              • Plataformas de pago
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Compartimos únicamente los datos indispensables y solo con proveedores que cumplen buenas prácticas de protección de datos.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}
          >
            🍪 5. Cookies y herramientas de análisis
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            En VOYBUS.COM usamos cookies para mejorar tu experiencia de navegación.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            También utilizamos herramientas como:
          </Typography>
          <Box sx={{ pl: { xs: 2, md: 3 }, mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              • <strong>Google Analytics</strong> – para entender cómo se usa el sitio y mejorar el contenido.
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
              • <strong>Meta Pixel (Facebook/Instagram)</strong> – para publicidad relevante y medir resultados de campañas.
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Estas herramientas pueden recopilar datos anónimos sobre tu navegación.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Siempre podés configurar o desactivar las cookies desde tu navegador.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Próximamente incorporaremos un aviso para que elijas tus preferencias de cookies.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}
          >
            📬 6. Comunicaciones comerciales
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Solo recibirás novedades, promociones o información de marketing si nos diste tu consentimiento.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Podés darte de baja en cualquier momento desde el link que aparece en cada mensaje o escribiéndonos.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}
          >
            🛡️ 7. Seguridad de tu información
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Tomamos medidas razonables para proteger tus datos y evitar accesos no autorizados.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Si bien ningún sistema es 100% infalible, trabajamos para cuidar tu información de la mejor forma.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}
          >
            🧑‍⚖️ 8. Tus derechos como usuario
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Podés solicitar en cualquier momento:
          </Typography>
          <Box sx={{ pl: { xs: 2, md: 3 }, mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              • Acceder a tus datos
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              • Modificar o actualizar información
            </Typography>
            <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              • Pedir que eliminemos tus datos
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
              • Retirar tu consentimiento para comunicaciones
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Solo tenés que escribirnos a: <Box component="span" sx={{ fontWeight: 600, color: 'primary.main' }}>hola@voybus.com</Box>
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}
          >
            🌍 9. Menores de edad
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Nuestros servicios están dirigidos a adultos.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Si recibimos datos de menores sin autorización, podremos eliminarlos.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}
          >
            ✏️ 10. Cambios en esta política
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Podemos actualizar esta Política de Privacidad cuando sea necesario.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            La versión vigente será siempre la que esté publicada en este sitio.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mt: 4, p: 3, backgroundColor: '#fff', borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Gracias por confiar en nosotros ❤️
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Si tenés dudas sobre esta política o cómo cuidamos tu información, escribinos a{' '}
            <Box component="span" sx={{ fontWeight: 600, color: 'primary.main' }}>
              hola@voybus.com
            </Box>
            {' '}— estamos para ayudarte.
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }} className="no-print">
            <Button
              variant="contained"
              color="primary"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              size="large"
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4,
                },
              }}
            >
              Imprimir PDF
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
    </>
  );
}

export default PoliticasPrivacidad;

